import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { ShieldCheck, CheckCircle2, Mail, LogOut } from 'lucide-react';

/**
 * Non-dismissible approval gate for NEW shippers.
 *
 * Shows when user_type === 'shipper' AND profiles.shipper_approved === false.
 * Admins and all other roles never see it. Polls the flag every 30s so the
 * shipper is released the moment we approve them in the DB — no refresh, no
 * re-login.
 *
 * NOTE: this is a UI gate only. Server-side write enforcement (RLS/RPC) is
 * intentionally a separate, later step.
 */
export const ShipperApprovalGate: React.FC = () => {
  const { user, profile, isAdmin, refreshProfile } = useAuth();
  const [approvedNow, setApprovedNow] = useState(false);
  const dismissTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isShipper = profile?.user_type === 'shipper';
  // Wait until the company-info form + welcome balloons are done
  // (documents_submitted flips true there) so the order stays:
  // info form → welcome → approval popup.
  const blocked =
    !isAdmin &&
    isShipper &&
    profile?.documents_submitted === true &&
    (profile as any)?.shipper_approved === false;

  // Poll for approval every 30s while blocked.
  useEffect(() => {
    if (!blocked || !user?.id || approvedNow) return;
    let cancelled = false;

    const check = async () => {
      const { data } = await supabase
        .from('profiles')
        .select('shipper_approved')
        .eq('id', user.id)
        .maybeSingle();
      if (cancelled) return;
      if ((data as any)?.shipper_approved === true) {
        setApprovedNow(true);
        // Brief confirmation, then refresh auth profile and unmount.
        dismissTimer.current = setTimeout(async () => {
          await refreshProfile();
        }, 2500);
      }
    };

    const interval = setInterval(check, 30000);
    return () => {
      cancelled = true;
      clearInterval(interval);
      if (dismissTimer.current) clearTimeout(dismissTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blocked, user?.id, approvedNow]);

  // Block Escape while the gate is up (no dismiss path).
  useEffect(() => {
    if (!blocked) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [blocked]);

  if (!blocked) return null;

  const handleSignOut = async () => {
    await supabase.auth.signOut({ scope: 'local' });
    window.location.href = '/auth';
  };

  if (approvedNow) {
    return (
      <div className="fixed inset-0 z-[200] flex items-center justify-center">
        <div className="absolute inset-0 bg-background/80 backdrop-blur-sm" />
        <div className="relative w-full max-w-sm mx-4 rounded-2xl border bg-card shadow-2xl p-8 flex flex-col items-center text-center gap-4 animate-in fade-in zoom-in-95 duration-300">
          <div className="w-14 h-14 rounded-full bg-success/10 border border-success/20 flex items-center justify-center">
            <CheckCircle2 className="w-7 h-7 text-success" />
          </div>
          <h2 className="text-lg font-bold text-foreground">
            You're approved — welcome aboard
          </h2>
          <p className="text-sm text-muted-foreground">
            Your account is unlocked. Loading your dashboard…
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="shipper-approval-title"
    >
      {/* Opaque backdrop — intentionally NOT click-to-close. */}
      <div className="absolute inset-0 bg-background/85 backdrop-blur-sm" />

      <div className="relative w-full max-w-md rounded-2xl border bg-card shadow-2xl p-7 flex flex-col items-center text-center gap-5 animate-in fade-in zoom-in-95 duration-300">
        <div className="absolute -top-px left-1/2 -translate-x-1/2 w-3/4 h-px bg-gradient-to-r from-transparent via-accent to-transparent" />

        <div className="w-16 h-16 rounded-xl bg-accent/10 border border-accent/20 flex items-center justify-center">
          <ShieldCheck className="w-8 h-8 text-accent" />
        </div>

        <div>
          <h2 id="shipper-approval-title" className="text-xl font-bold text-foreground">
            We're confirming your account
          </h2>
          <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
            To keep our network trusted, a LaneRunner representative will confirm your
            company details before you start posting. This usually takes 2–24 hours.
            We'll reach out shortly.
          </p>
        </div>

        <div className="w-full rounded-lg border bg-muted/30 p-3 text-left">
          <p className="text-xs text-muted-foreground">
            Nothing else is needed from you right now. This screen will unlock
            automatically as soon as your account is approved.
          </p>
        </div>

        <div className="w-full flex flex-col gap-2">
          <a
            href="mailto:support@lanerunner.com"
            className="inline-flex items-center justify-center gap-2 text-sm font-semibold text-brand-teal-deep hover:underline"
          >
            <Mail className="w-4 h-4" /> Questions? support@lanerunner.com
          </a>
          <Button variant="outline" onClick={handleSignOut} className="w-full h-10">
            <LogOut className="w-4 h-4 mr-2" /> Sign out
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ShipperApprovalGate;
